import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  UserRoundPen,
  Star,
  Lightbulb,
  Search,
  FileCheck,
  User,
  MapPin,
  Moon,
  Smile,
  Save,
  ChevronDown,
  ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useDraftStorage } from "@/hooks/useDraftStorage";
import { sanitizeText } from "@/lib/security/sanitize";

interface InterviewQuestion {
  q: string;
  tip: string;
}

interface QuestionCategory {
  category: string;
  questions: InterviewQuestion[];
}

const interviewQuestions: QuestionCategory[] = [
  {
    category: 'Behavioral Questions',
    questions: [
      { 
        q: "Tell me about a time you had to handle a difficult coworker.", 
        tip: "Focus on professionalism, empathy, and a positive resolution. Avoid speaking negatively about the coworker." 
      },
      { 
        q: "Describe a situation where you had to learn a new skill quickly.", 
        tip: "Showcase your adaptability, learning process, and how you applied the new skill." 
      },
      { 
        q: "Give an example of a goal you reached and how you achieved it.", 
        tip: "Detail your planning, execution, and the positive outcome. Use metrics if possible." 
      },
      { 
        q: "Tell me about a time you failed. What did you learn?", 
        tip: "Be honest but focus on accountability and what you learned from the experience. Show resilience." 
      }
    ]
  },
  {
    category: 'Situational Questions',
    questions: [
      { 
        q: "What would you do if you were assigned a task you didn't know how to complete?", 
        tip: "Emphasize your resourcefulness: researching, asking for guidance, and breaking down the problem." 
      },
      { 
        q: "How would you handle a tight deadline that you don't think you can meet?", 
        tip: "Focus on proactive communication, prioritization, and managing expectations." 
      },
      { 
        q: "Imagine a client is unhappy with your work. How would you respond?", 
        tip: "Show your commitment to customer satisfaction: listen, empathize, and propose solutions." 
      }
    ]
  },
  {
    category: 'Questions about You',
    questions: [
      { 
        q: "What are your greatest strengths?", 
        tip: "Choose 2-3 strengths relevant to the job. Provide a brief example for each." 
      },
      { 
        q: "What are your greatest weaknesses?", 
        tip: "Be genuine. Choose a real weakness, but show how you are actively working to improve it." 
      },
      { 
        q: "Where do you see yourself in five years?", 
        tip: "Align your goals with the company's potential career paths. Show ambition and commitment." 
      },
      { 
        q: "Why do you want to work for this company?", 
        tip: "Be specific. Mention their values, products, or mission that resonates with you." 
      }
    ]
  }
];

interface InterviewDraft {
  pitch: string;
  answers: Record<string, string>;
}

const InterviewPrep = () => {
  const navigate = useNavigate();
  const [pitch, setPitch] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<Record<string, boolean>>({});

  // Use secure database storage
  const {
    data: savedInterview,
    setData: setSavedInterview,
    isLoading: isDraftLoading,
  } = useDraftStorage<InterviewDraft>({
    draftType: 'interview',
    autoSaveInterval: 0, // Manual save only
    expiresInDays: 7,
  });

  // Load draft on mount
  useEffect(() => {
    if (savedInterview) {
      if (savedInterview.pitch) setPitch(savedInterview.pitch);
      if (savedInterview.answers) setAnswers(savedInterview.answers);
    }
  }, [savedInterview]);

  const saveToLocalStorage = async (id: string, value: string) => {
    const sanitizedValue = sanitizeText(value);
    const updatedAnswers = { ...answers, [id]: sanitizedValue };
    setAnswers(updatedAnswers);
    
    await setSavedInterview({
      pitch,
      answers: updatedAnswers,
    });
    
    setSaveStatus({ ...saveStatus, [id]: true });
    setTimeout(() => {
      setSaveStatus({ ...saveStatus, [id]: false });
    }, 2000);
  };

  const savePitch = async () => {
    const sanitizedPitch = sanitizeText(pitch);
    setPitch(sanitizedPitch);
    
    await setSavedInterview({
      pitch: sanitizedPitch,
      answers,
    });
    
    setSaveStatus({ ...saveStatus, pitch: true });
    setTimeout(() => {
      setSaveStatus({ ...saveStatus, pitch: false });
    }, 2000);
  };

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      {/* Header */}
      <header className="bg-card shadow-sm sticky top-0 z-10 border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="hover-scale"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-3xl font-bold text-foreground">Interview Prep Hub</h1>
            </div>
            <div className="flex items-center space-x-2 text-accent">
              <Briefcase className="h-5 w-5" />
              <span className="hidden md:block font-semibold">Land Your Dream Job</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 flex-1">
        
        {/* Introduction */}
        <section className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl font-bold mb-4 text-foreground">Preparation is the key to success.</h2>
          <p className="max-w-3xl mx-auto text-lg text-muted-foreground">
            Welcome to your personal interview preparation space. Use these tools to practice your answers, master common questions, and walk into your next interview with confidence.
          </p>
        </section>

        {/* Craft Your Story Section */}
        <section className="bg-card p-8 rounded-xl shadow-[var(--shadow-card)] border border-border mb-12 animate-fade-in hover:shadow-[var(--shadow-elevated)] transition-shadow">
          <div className="flex items-start space-x-6">
            <div className="bg-accent/10 text-accent p-3 rounded-full flex-shrink-0">
              <UserRoundPen className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-2 text-foreground">Craft Your "Tell me about yourself" Pitch</h3>
              <p className="text-muted-foreground mb-4">This is your first impression. Structure a concise and compelling story that connects your experience to the role you're applying for. Aim for 60-90 seconds.</p>
              <div className="bg-muted/50 p-4 rounded-lg">
                <Textarea
                  value={pitch}
                  onChange={(e) => setPitch(e.target.value)}
                  className="min-h-[160px] resize-none"
                  placeholder="Draft your professional story here..."
                  maxLength={1000}
                />
                <div className="flex justify-between items-center mt-3">
                  <span className={`text-sm text-accent transition-opacity ${saveStatus.pitch ? 'opacity-100' : 'opacity-0'}`}>
                    Saved!
                  </span>
                  <Button onClick={() => void savePitch()} className="flex items-center gap-2 hover-scale">
                    <Save className="h-4 w-4" />
                    Save Pitch
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* STAR Method Section */}
        <section className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-8 rounded-xl shadow-[var(--shadow-elevated)] mb-12 animate-fade-in border border-primary/20">
          <div className="flex items-center space-x-6 mb-6">
            <Star className="h-12 w-12 text-accent" />
            <div>
              <h3 className="text-3xl font-bold mb-2">Master the STAR Method</h3>
              <p className="text-primary-foreground/90">Use this framework to structure compelling answers for behavioral questions ("Tell me about a time when...").</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-primary-foreground/10 backdrop-blur-sm p-4 rounded-lg hover:bg-primary-foreground/15 transition-colors">
              <h4 className="font-bold text-lg text-accent mb-2">Situation</h4>
              <p className="text-primary-foreground/90 text-sm">Set the scene and provide necessary context.</p>
            </div>
            <div className="bg-primary-foreground/10 backdrop-blur-sm p-4 rounded-lg hover:bg-primary-foreground/15 transition-colors">
              <h4 className="font-bold text-lg text-accent mb-2">Task</h4>
              <p className="text-primary-foreground/90 text-sm">Describe your responsibility or goal in that situation.</p>
            </div>
            <div className="bg-primary-foreground/10 backdrop-blur-sm p-4 rounded-lg hover:bg-primary-foreground/15 transition-colors">
              <h4 className="font-bold text-lg text-accent mb-2">Action</h4>
              <p className="text-primary-foreground/90 text-sm">Explain the specific steps you took to address it.</p>
            </div>
            <div className="bg-primary-foreground/10 backdrop-blur-sm p-4 rounded-lg hover:bg-primary-foreground/15 transition-colors">
              <h4 className="font-bold text-lg text-accent mb-2">Result</h4>
              <p className="text-primary-foreground/90 text-sm">Share the outcomes of your actions, quantifying if possible.</p>
            </div>
          </div>
        </section>

        {/* Common Questions Section */}
        <section className="mb-12 animate-fade-in">
          <h3 className="text-3xl font-bold text-center mb-8 text-foreground">Practice Common Questions</h3>
          <div className="space-y-6">
            {interviewQuestions.map((cat, catIndex) => (
              <div key={catIndex}>
                <h4 className="text-xl font-bold text-foreground mb-4">{cat.category}</h4>
                <div className="space-y-4">
                  {cat.questions.map((item, itemIndex) => {
                    const id = `q-${catIndex}-${itemIndex}`;
                    const isOpen = openAccordion === id;
                    
                    return (
                      <div key={id} className="bg-card rounded-lg shadow-[var(--shadow-card)] border border-border hover:shadow-[var(--shadow-elevated)] transition-shadow">
                        <button
                          onClick={() => toggleAccordion(id)}
                          className="w-full text-left p-5 flex justify-between items-center hover:bg-accent/5 transition-colors rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                          <span className="font-semibold text-lg pr-4 text-foreground">{item.q}</span>
                          <ChevronDown className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 text-accent ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[600px]' : 'max-h-0'}`}>
                          <div className="p-5 border-t border-border">
                            <p className="text-sm text-accent bg-accent/10 p-3 rounded-md mb-4">
                              <span className="font-semibold">Tip:</span> {item.tip}
                            </p>
                            <Textarea
                              value={answers[id] || ""}
                              onChange={(e) => setAnswers({ ...answers, [id]: e.target.value })}
                              className="min-h-[128px] resize-none"
                              placeholder="Draft your STAR-based answer here..."
                              maxLength={2000}
                            />
                            <div className="flex justify-between items-center mt-3">
                              <span className={`text-sm text-accent transition-opacity ${saveStatus[id] ? 'opacity-100' : 'opacity-0'}`}>
                                Saved!
                              </span>
                              <Button
                                variant="secondary"
                                onClick={() => void saveToLocalStorage(id, answers[id] || "")}
                                className="flex items-center gap-2 hover-scale"
                              >
                                <Save className="h-4 w-4" />
                                Save Answer
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Questions for Them Section */}
        <section className="bg-card p-8 rounded-xl shadow-[var(--shadow-card)] border border-border mb-12 animate-fade-in hover:shadow-[var(--shadow-elevated)] transition-shadow">
          <div className="flex items-start space-x-6">
            <div className="bg-accent/10 text-accent p-3 rounded-full flex-shrink-0">
              <Lightbulb className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2 text-foreground">Prepare Questions to Ask Them</h3>
              <p className="text-muted-foreground mb-4">Asking thoughtful questions shows your interest and helps you evaluate if the company is a good fit for you. Here are some ideas:</p>
              <ul className="list-disc list-inside space-y-2 text-foreground/90">
                <li>"What does success look like in this role within the first 90 days?"</li>
                <li>"What are the biggest challenges the team is currently facing?"</li>
                <li>"Can you describe the company culture and the type of people who thrive here?"</li>
                <li>"What are the opportunities for professional growth and development?"</li>
                <li>"What are the next steps in the interview process?"</li>
              </ul>
            </div>
          </div>
        </section>
        
        {/* Final Tips */}
        <section className="animate-fade-in">
          <h3 className="text-3xl font-bold text-center mb-8 text-foreground">Final Checklist for Success</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-card p-6 rounded-lg shadow-[var(--shadow-card)] border border-border flex items-center space-x-4 hover:shadow-[var(--shadow-elevated)] transition-shadow hover-scale">
              <div className="bg-accent/10 text-accent p-3 rounded-full flex-shrink-0">
                <Search className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Research the Company</h4>
                <p className="text-sm text-muted-foreground">Know their mission, products, and recent news.</p>
              </div>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-[var(--shadow-card)] border border-border flex items-center space-x-4 hover:shadow-[var(--shadow-elevated)] transition-shadow hover-scale">
              <div className="bg-accent/10 text-accent p-3 rounded-full flex-shrink-0">
                <FileCheck className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Review Your Resume</h4>
                <p className="text-sm text-muted-foreground">Be ready to discuss every point.</p>
              </div>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-[var(--shadow-card)] border border-border flex items-center space-x-4 hover:shadow-[var(--shadow-elevated)] transition-shadow hover-scale">
              <div className="bg-accent/10 text-accent p-3 rounded-full flex-shrink-0">
                <User className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Prepare Your Outfit</h4>
                <p className="text-sm text-muted-foreground">Choose professional attire the day before.</p>
              </div>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-[var(--shadow-card)] border border-border flex items-center space-x-4 hover:shadow-[var(--shadow-elevated)] transition-shadow hover-scale">
              <div className="bg-primary/10 text-primary p-3 rounded-full flex-shrink-0">
                <MapPin className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Confirm Logistics</h4>
                <p className="text-sm text-muted-foreground">Check location, time, and interviewer names.</p>
              </div>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-[var(--shadow-card)] border border-border flex items-center space-x-4 hover:shadow-[var(--shadow-elevated)] transition-shadow hover-scale">
              <div className="bg-primary/10 text-primary p-3 rounded-full flex-shrink-0">
                <Moon className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Get a Good Night's Sleep</h4>
                <p className="text-sm text-muted-foreground">Rest is crucial for a sharp mind.</p>
              </div>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-[var(--shadow-card)] border border-border flex items-center space-x-4 hover:shadow-[var(--shadow-elevated)] transition-shadow hover-scale">
              <div className="bg-accent/10 text-accent p-3 rounded-full flex-shrink-0">
                <Smile className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Be Positive & Confident</h4>
                <p className="text-sm text-muted-foreground">You've prepared for this. You've got it!</p>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default InterviewPrep;
